import { Flex, Group, IconFont, Modal, Text, Toast, useDisclosure } from '@byecode/ui'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import type { GroupPage, PageAbstract, PageItem } from '@lighthouse/core'
import { TooltipText } from '@lighthouse/shared'
import cls from 'classnames'
import { useAtomCallback } from 'jotai/utils'
import { find, min, path } from 'rambda'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'

import { pageTreeListAtom } from '@/atoms/page/state'
import type { CreatePagePayload, MovePagePayload } from '@/atoms/page/types'
import { findByPageByTree, pageTreeToPageList } from '@/atoms/utils/page'

import { CreatePage } from '../../CreatePage'
import { PageOperation } from '../Operation'

const SCxIconWrapper = styled(Flex)`
    opacity: 0;
    flex-shrink: 0;
`

const SCxContainer = styled.div`
    width: 100%;
    cursor: pointer;
`

const SCxItem = styled(Flex)`
    width: 100%;
    display: flex;
    align-items: center;
    padding: 0 8px;
    height: 32px;
    border-radius: 6px;
    &:hover {
        background-color: var(--color-gray-100);
        ${SCxIconWrapper} {
            opacity: 1;
        }
    }
`
const SCxTitleInput = styled.div`
    flex: 1;
    height: 20px;
    line-height: 20px;
    font-size: var(--font-size-normal);
    overflow: hidden;

    input {
        width: 100%;
    }
`

interface PageGroupProps {
    tree: GroupPage
    focusPage?: string
    activeId?: string
    openedMap: Record<string, boolean>
    style?: React.CSSProperties
    onUpdatePage?: (param: Partial<PageAbstract> & { id: string }) => Promise<boolean>
    onSetHomePage?: (param: Pick<PageAbstract, 'id' | 'isHome'>) => Promise<boolean>
    onRemovePage?: (id: string) => void
    onToLink?: (page: PageAbstract) => void
    onChangeEdit: (id: string) => void
    onCopyPage: (id: string, lang: string, groupId?: string) => Promise<string>
    onMovePage: (params: MovePagePayload) => void
    onAdd: (v: CreatePagePayload) => void
    onToggleCollapse: (id: string, open?: boolean) => void
}

export const PageGroup: React.FC<PageGroupProps> = ({ tree, style: propsStyle, ...rest }) => {
    const { activeId, focusPage, openedMap, onAdd, onCopyPage, onRemovePage, onUpdatePage, onChangeEdit, onToggleCollapse } = rest
    const inputRef = useRef<HTMLInputElement>(null)
    const [pageName, setPageName] = useState(tree.name)
    const opened = useMemo(() => openedMap[tree.id], [openedMap, tree.id])

    const isEdit = focusPage === tree.id
    const { attributes, listeners, setNodeRef, active, transform, transition, isOver } = useSortable({
        id: tree.id,
        data: {
            type: 'GROUP'
        }
    })

    const style = useMemo(() => {
        const maxX = (tree.level ?? 0) * 48 + 48
        return {
            transform: CSS.Transform.toString(transform && { ...transform, scaleY: 1, x: min(transform.x, maxX) }),
            transition,
            ...propsStyle
        }
    }, [propsStyle, transform, transition, tree.level])

    useEffect(() => {
        const flatPageList = pageTreeToPageList(tree.children, [])
        if (flatPageList.some(item => item?.id === activeId)) {
            onToggleCollapse?.(tree.id, true)
        }
    }, [activeId, tree.children, onToggleCollapse, tree.id])

    const handleUpdatePage = useCallback(async () => {
        onChangeEdit(tree.id)
        if (tree.name === pageName) {
            return
        }
        if (!pageName.trim()) {
            setPageName(tree.name)
            return
        }
        const isUpdate = await onUpdatePage?.({ id: tree.id, name: pageName ?? '未命名' })
        if (!isUpdate) {
            setPageName(tree.name)
        }
    }, [onChangeEdit, tree.id, tree.name, pageName, onUpdatePage])

    const handleKeyDown = useCallback(
        (ev: React.KeyboardEvent) => {
            if (ev.key === 'Enter') {
                handleUpdatePage()
            }
        },
        [handleUpdatePage]
    )

    const handleRemovePageGroup = useAtomCallback(
        useCallback(
            async get => {
                const pageTrees = get(pageTreeListAtom)
                const currentTree = findByPageByTree(pageTrees)(tree.id)
                const pageList = currentTree ? pageTreeToPageList([currentTree], []) : []
                if (pageList.some(item => item.isHome)) {
                    Toast.warning('删除失败，因为删除的页面中包含主页')
                    return
                }
                if (pageList.length > 0) {
                    const isConfirm = await Modal.confirm({
                        title: '确认删除',
                        content: '删除后分组下的页面和分组都会被删除，且不可恢复，请谨慎操作。',
                        okStatus: 'error',
                        okText: '删除',
                        cancelText: '取消'
                    })
                    isConfirm && onRemovePage?.(tree.id)
                }
                onRemovePage?.(tree.id)
            },
            [onRemovePage, tree.id]
        )
    )
    useEffect(() => {
        if (inputRef && inputRef.current && isEdit) {
            inputRef.current.select()
        }
    }, [isEdit])

    return (
        <SCxContainer style={style} ref={setNodeRef} {...attributes} {...listeners}>
            <SCxItem gap={6} style={{ paddingLeft: 12 + (tree?.level ?? 0) * 20 }}>
                <Flex gap={4} alignItems="center" style={{ flex: 1, overflow: 'hidden' }}>
                    <IconFont
                        type="ArrowDownSmall"
                        size={16}
                        color="var(--color-gray-400)"
                        style={{
                            transform: opened ? undefined : 'rotate(-90deg)',
                            transition: 'transform 0.2s',
                            userSelect: 'none'
                        }}
                        onClick={() => onToggleCollapse(tree.id, !opened)}
                    />
                    <IconFont type="Folder" size={16} color="var(--color-theme-7)" />
                    {isEdit ? (
                        <SCxTitleInput>
                            <input
                                type="text"
                                value={pageName}
                                placeholder="请输入名称"
                                autoFocus
                                onKeyDown={handleKeyDown}
                                onChange={e => setPageName(e.target.value)}
                                onBlur={() => handleUpdatePage()}
                                ref={inputRef}
                            />
                        </SCxTitleInput>
                    ) : (
                        <TooltipText
                            title={tree.name}
                            render={ref => (
                                <Text
                                    ref={ref}
                                    weight={400}
                                    size={14}
                                    color="var(--color-gray-700)"
                                    onDoubleClick={() => onChangeEdit(tree.id)}
                                >
                                    {tree.name}
                                </Text>
                            )}
                        />
                    )}
                </Flex>
                <SCxIconWrapper justifyContent="flex-end" gap={6} className={cls({ open: opened })}>
                    <PageOperation
                        tree={tree}
                        onCopyPage={(id, language) => {
                            onCopyPage(id, language, language === tree.language ? tree.parentId : undefined)
                        }}
                        onRemovePage={handleRemovePageGroup}
                    />
                    <CreatePage groupId={tree.id} onAdd={onAdd} />
                </SCxIconWrapper>
                {/* {isOver && active?.data.current?.type === 'folder' && <DropIndicator depth={tree.level ?? 0} position="inside" />} */}
            </SCxItem>
            {/* {isOver && active?.data.current?.type !== 'folder' && (
                <DropIndicator depth={tree.level ?? 0} position={path(['data', 'dropPosition'], active) || 'bottom'} />
            )} */}
        </SCxContainer>
    )
}
